import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Paper from 'material-ui/Paper';
import * as Colors from 'material-ui/styles/colors';
import _ from 'lodash';
import {
  Table, search, sort, highlight, resolve,
  SearchColumns, resizableColumn, Provider, Body
} from 'reactabular-table';
import * as Virtualized from 'shared/vendor/reactabular-virtualized/src/index';
import transitions from 'material-ui/styles/transitions';
import Dimensions from 'react-dimensions';
import Checkbox from 'material-ui/Checkbox';
import ChildrenFader from 'shared/components/ChildrenFader';
import IntermediateCheckbox from 'shared/components/IntermediateCheckbox';
import {filter as fuzzyFilter} from 'shared/helpers/filterHelper';
import {Scrollbars} from 'shared/vendor/react-custom-scrollbars/src/index';
import VirtualizedListViewSortDropDownMenu from './VirtualizedListViewSortDropDownMenu';
import SearchIcon from 'material-ui/svg-icons/action/search';
import {defaultMemoize} from 'reselect';
import update from 'immutability-helper';

const tableStyles = require('shared/components/TableHelpers/TableStyles.scss');

const styles = {
  tableRow: {
    borderTop: `1px solid ${Colors.grey300}`,
    color: Colors.grey700,
    width: '100%',
    transition: transitions.easeOut('150ms'),
    display: 'flex',
  },
  checkbox: {
    marginLeft: 24,
    transition: transitions.easeOut('150ms'),
  },
};

class Cell extends Component {
  render() {
    const {style} = this.props;
    return (
      <td
        {...this.props}
        style = {{display: 'flex', alignItems: 'center', ...style}}
      />
    );
  }
}

Cell.defaultProps = {
  style: {},
};

class VirtualizedListView extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    muiTheme: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    const {columns, selectedItemIds} = this.props;
    window.ItemsTable = this;
    this.state = {
      tableBodyRef: {},
      headerZDepth: 0,
      selectedItemIds: selectedItemIds || {},
      selectedItemsCount: _.size(selectedItemIds) || 0,
      sortField: 0,
      sortAscending: false,
    };
    this.itemsArray = this.getSortedItems(props, this.state);
    this.columns = _.map(columns, column => {
      return update(column, {
        cell: {
          formatters: {
            $apply: formatters => {
              return formatters.map(formatter => {
                return (value, data) => {
                  const {idField} = this.props;
                  const id = data.rowData[idField];
                  const {hoveredItemId} = this.state;
                  return formatter(value, data, hoveredItemId === id);
                };
              });
            },
          },
        },
      });
    });
  }

  componentWillReceiveProps(nextProps) {
    const {path} = nextProps;
    if (this.props.path !== path) {
      this.setState({headerZDepth: 0});
    }
    if (nextProps.selectedItemIds) {
      this.setState({
        selectedItemIds: nextProps.selectedItemIds,
        selectedItemsCount: _.size(nextProps.selectedItemIds),
      });
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const {items} = nextProps;
    const {sortAscending, sortField} = nextState;
    if (this.props.items !== items || this.state.sortField !== sortField || this.state.sortAscending !== sortAscending) {
      this.itemsArray = this.getSortedItems(nextProps, nextState);
    }

  }

  onScroll = (event) => {
    const {virtualized} = this.props;
    if (!virtualized) {
      return;
    }
    const {tableBodyRef, headerZDepth} = this.state;
    const { target: { scrollTop } } = event;
    if (scrollTop > 20 && headerZDepth === 0) {
      this.setState({headerZDepth: 1});
    } else if (scrollTop < 30 && headerZDepth === 1) {
      this.setState({headerZDepth: 0});
    }
    tableBodyRef.onScroll(event);
  };

  getSortedItems(props, state) {
    const {itemsCount, sortFields, firstItem} = props;
    const {sortAscending, sortField} = state;
    let {items} = props;
    items = _.values(items);
    const orders = sortAscending ? ['asc'] : ['desc'];
    if (sortFields[sortField] && sortFields[sortField].iteratees) {
      items = _.orderBy(items, sortFields[sortField].iteratees, orders);
    }
    if (firstItem) {
      items.unshift(firstItem);
    }
    if (itemsCount) {
      items.length = itemsCount;
    }
    return items;
  }

  getFilteredItems = defaultMemoize((props, itemsArray) => {
    const {filter, filterFields} = props;
    let filteredItems;
    if (_.isEmpty(filter)) {
      filteredItems = itemsArray;
    } else {
      filteredItems = fuzzyFilter(itemsArray, filter, filterFields);
    }
    return filteredItems;
  });

  scrollTo(index) {
    const {tableBodyRef} = this.state;
    const {scrollableArea} = this.refs;
    const startHeight = tableBodyRef.getRef().scrollTo(index);
    scrollableArea.refs.view.scrollTop = startHeight;
    tableBodyRef.onScroll({target: {scrollTop: startHeight}});
  }

  selectedColumn = {
    property: 'select',
    props: {
    },
    header: {
      label: 'Selected'
    },
    cell: {
      formatters: [(id) => {
        const {selectedItemIds} = this.state;
        const selected = !!selectedItemIds[id];
        const {hoveredItemId} = this.state;
        const visible = selected || hoveredItemId === id;
        return (
          <div style={{marginLeft: 24, width: 40}}>
            <ChildrenFader visible={visible}>
              {
                visible &&
                <Checkbox
                  className = {tableStyles.fadedWhenNotHovered}
                  style = {{opacity: selected ? 1 : undefined}}
                  checked={selected}
                  onCheck = {(event, checked) => {
                    this.props.setSelectedItemIds({
                      ...selectedItemIds,
                      [id]: checked,
                    });
                  }}
                />
              }
            </ChildrenFader>
          </div>
        );
      }],
    }
  };

  handleRowClicked = (id, rowIndex, event) => {
    const {setSelectedItemIds, idField} = this.props;
    const {selectedItemIds} = this.state;
    const selected = !selectedItemIds[id];
    if (event.shiftKey && this.lastSelectedRowIndex !== null) {
      const startIndex = Math.min(this.lastSelectedRowIndex, rowIndex);
      const endIndex = Math.max(this.lastSelectedRowIndex, rowIndex);
      const filteredItems = this.getFilteredItems(this.props, this.itemsArray);
      const addedSelectedItemIds = {};
      _.range(startIndex, endIndex + 1).forEach(rowIndexToSelect => {
        addedSelectedItemIds[filteredItems[rowIndexToSelect][idField]] = true;
      });
      setSelectedItemIds({
        ...selectedItemIds,
        ...addedSelectedItemIds,
      });
    } else if (event.ctrlKey) {
      setSelectedItemIds({
        ...selectedItemIds,
        [id]: selected,
      });
    } else {
      setSelectedItemIds({
        [id]: selected,
      });
    }
    this.lastSelectedRowIndex = selected ? rowIndex : null;
  };

  handleRowMouseEnter = (id) => {
    this.setState({hoveredItemId: id});
  };

  handleRowMouseLeave = (id) => {
    if (this.state.hoveredItemId === id) {
      this.setState({hoveredItemId: null});
    }
  };

  selectAll = () => {
    const {idField} = this.props;
    const selectedItemIds = {};
    _.forEach(this.getFilteredItems(this.props, this.itemsArray), (item) => {
      if (item) {
        selectedItemIds[item[idField]] = true;
      }
    });
    this.props.setSelectedItemIds(selectedItemIds);
  };

  handleCheck = (event, checked) => {
    const {setSelectedItemIds} = this.props;
    if (checked) {
      this.selectAll();
    } else {
      setSelectedItemIds({});
    }
  };

  renderEmptySearch() {
    return (<div style={{padding: 50, display: 'flex', justifyContent: 'center'}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <SearchIcon style={{marginRight: 10}}/>
        No items matched your search. Try a different search...
      </div>r
    </div>);
  }

  render() {
    const {notification, sortFields, containerHeight, path, selectable, idField, style,
          virtualized, rowHeight, showHeader, hideSortBlock, containerStyle, headerStyle} = this.props;
    const {sortField, sortAscending} = this.state;
    let {columns} = this;
    if (selectable) {
      columns = [this.selectedColumn, ...columns];
    }
    const {selectedItemsCount, selectedItemIds, tableBodyRef, hoveredItemId, headerZDepth} = this.state;
    const {itemsArray} = this;
    const filteredItems = this.getFilteredItems(this.props, itemsArray);
    const allSelected = selectedItemsCount === filteredItems.length;

    let emptyStateContent;
    if (filteredItems.length === 0 && itemsArray.length > 0) {
      emptyStateContent = this.renderEmptySearch();
    }
    let headerHeight = this.props.headerHeight;
    if (notification && notification.props.open) {
      headerHeight += 80;
    }
    return (
      <ChildrenFader
        path = {path}
        style={{position: 'relative', minWidth: 200, ...style}}
      >
        {
          emptyStateContent
            ?
            emptyStateContent
            :
            [
              <div
                key={`${path}0`}
                style = {{
                  zIndex: 2,
                  position: 'absolute',
                  top: 0,
                  paddingLeft: 30,
                  paddingRight: 40,
                  left: 0,
                  right: 20,
                  height: headerHeight,
                  transition: transitions.easeOut(),
                  backgroundColor: Colors.grey200,
                  overflow: 'visible',
                  ...headerStyle,
                }}
              >
                <div
                  style={{paddingBottom: 15, overflow: 'hidden'}}
                >
                  <Paper
                    style = {{
                      width: `100%`,
                      height: headerHeight,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      flexDirection: 'column',
                      backgroundColor: Colors.grey200,
                      transitionDuration: headerZDepth === 0 ? 0 : undefined,
                    }}
                    zDepth={headerZDepth}
                  >
                    {notification}
                    <div
                      style={{
                        marginTop: 10,
                        width: `100%`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: 48,
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{height: '100%', display: 'flex', alignItems: 'center'}}
                      >
                        {
                          selectable &&
                          <IntermediateCheckbox
                            checked = {selectedItemsCount > 0}
                            intermediate={!allSelected}
                            onCheck = {this.handleCheck}
                            className = {tableStyles.fadedWhenNotHovered}
                            style = {{...styles.checkbox, opacity: selectedItemsCount > 0 ? 1 : undefined}}
                          />
                        }
                      </div>
                      {
                        showHeader &&
                        _.map(this.columns, (item, index) => {
                          return (
                            <div
                              key={index}
                              style = {{
                                ...item.props.style,
                              }}
                            >
                              {item.header.label}
                            </div>);
                        })
                      }
                      {
                        !hideSortBlock &&
                        <VirtualizedListViewSortDropDownMenu
                          onChange = {change => this.setState(change)}
                          sortFields = {sortFields}
                          sortField = {sortField}
                          sortAscending = {sortAscending}
                        />
                      }
                    </div>
                  </Paper>
                </div>
              </div>,
              <Scrollbars
                key={`${path}1`}
                style = {{height: containerHeight, overflowY: 'hidden'}}
                onScroll={this.onScroll}
                renderThumbVertical = {({style, ...props}) => {
                  return <div style={{...style, backgroundColor: Colors.black, opacity: 0.3, borderRadius: 3}}></div>;
                }}
                ref="scrollableArea"
              >
                <div
                  style = {{paddingTop: headerHeight + 20, paddingLeft: 30, paddingRight: 60, overflowY: 'auto', ...containerStyle}}
                  onClick={e => e.stopPropagation()}
                >
                  <div>
                    <div
                      style={{
                        transition: transitions.easeOut(),
                        boxShadow: this.context.muiTheme.paper.zDepthShadows[1],
                        marginBottom: 30,
                      }}
                    >
                      <Provider
                        columns={columns}
                        components={{
                          body: virtualized ? {
                            wrapper: Virtualized.BodyWrapper,
                            row: Virtualized.BodyRow,
                            cell: Cell,
                          } : {
                            cell: Cell,
                            row: Virtualized.BodyRow,
                          },
                        }}
                        style = {{
                          width: '100%',
                          backgroundColor: Colors.white,
                        }}
                      >
                        {
                          React.createElement(virtualized ? Virtualized.Body : Body,
                            {
                              rows: filteredItems,
                              rowHeight,
                              rowKey: idField,
                              height: _.isNumber(rowHeight) ? Math.min(containerHeight - headerHeight, filteredItems.length * rowHeight) : undefined,
                              ref: (ref) => {
                                if (ref && ref !== tableBodyRef) {
                                  this.setState({tableBodyRef: ref});
                                }
                              },
                              style: {
                                backgroundColor: Colors.white,
                                transition: transitions.easeOut('300ms'),
                                overflowY: _.isNumber(rowHeight) && containerHeight - headerHeight > filteredItems.length * rowHeight ? 'hidden' : undefined
                              },
                              dataContext: {selectedItemIds, hoveredItemId},
                              onRow: (item, {rowIndex, startIndex}) => {
                                const {selectable} = this.props;
                                const id = item[idField];
                                const selected = selectedItemIds[id];
                                const classes = [tableStyles.tableRow];
                                if (selected) {
                                  classes.push(tableStyles.selectedTableRow);
                                }
                                const rowProps = {
                                  style: {...styles.tableRow, height: rowHeight},
                                  className: classes.join(' '),
                                  onMouseEnter: this.handleRowMouseEnter.bind(null, id),
                                  onMouseLeave: this.handleRowMouseLeave.bind(null, id),
                                };
                                if (selectable) {
                                  rowProps.onClick = this.handleRowClicked.bind(null, id, rowIndex + startIndex);
                                }
                                return rowProps;
                              }
                            })
                        }
                      </Provider>
                    </div>
                  </div>
                </div>
              </Scrollbars>,
            ]
        }
      </ChildrenFader>
    );
  }
}

VirtualizedListView.propTypes = {
  selectable: PropTypes.bool.isRequired,
  idField: PropTypes.string.isRequired,
  sortFields: PropTypes.array.isRequired,
  filterFields: PropTypes.array,
  filter: PropTypes.string,
  virtualized: PropTypes.bool,
  rowHeight: PropTypes.any,
  showHeader: PropTypes.bool,
  hideSortBlock: PropTypes.bool,
  headerHeight: PropTypes.number,
};

VirtualizedListView.defaultProps = {
  selectable: false,
  idField: 'id',
  virtualized: true,
  rowHeight: 99,
  firstItem: null,
  headerHeight: 65,
  showHeader: false,
  hideSortBlock: false,
};

export default Dimensions()(VirtualizedListView);